import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Feather, Moon, Sparkles, ShieldCheck } from "lucide-react";
import { LanguageSelector } from "@/components/LanguageSelector";
import { InstallPWA } from "@/components/InstallPWA";

const FEATURE_ICONS = [Feather, Sparkles, Moon];

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const features = t("landing.features", { returnObjects: true });
  return (
    <div className="anima-backdrop min-h-screen w-full flex justify-center">
      <div data-testid="landing-container" className="w-full max-w-md mx-auto px-6 pt-8 pb-5 flex flex-col min-h-screen">
        <div className="flex justify-end mb-2">
          <LanguageSelector />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="flex flex-col"
        >
          <div className="anima-float w-16 h-16 mb-4">
            <img src="/flame-emblem.webp" alt="Ascolta Anima" className="w-full h-full object-contain" draggable={false} />
          </div>

          <h1 data-testid="landing-title" className="font-serif text-[clamp(36px,13.5vw,52px)] leading-[0.95] whitespace-nowrap text-anima-strong">Ascolta Anima</h1>
          <p className="text-anima-strong text-lg mt-2 leading-snug">
            {t("landing.tagline")}
          </p>
          <p className="text-anima-soft text-sm mt-1.5 tracking-wide">
            {t("landing.creatorPre")}{" "}
            <button
              onClick={() => navigate("/chi-sono")}
              className="underline decoration-anima-gold/60 underline-offset-2 text-anima-strong"
              data-testid="landing-anna-link"
            >
              Anna Checchi
            </button>
          </p>

          <p className="text-anima-strong text-base leading-relaxed mt-4">
            {t("landing.intro")}
          </p>

          <div className="space-y-3 mt-6">
            {features.map((f, i) => {
              const Icon = FEATURE_ICONS[i] || Feather;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + i * 0.12, duration: 0.7 }}
                  className="flex gap-3.5 items-start"
                >
                  <span className="mt-0.5 p-2 rounded-full bg-anima-pink/60 shrink-0">
                    <Icon size={17} strokeWidth={1.6} className="text-anima-strong" />
                  </span>
                  <div>
                    <p className="font-serif text-lg text-anima-strong leading-tight">{f.t}</p>
                    <p className="text-sm text-anima-soft leading-snug">{f.d}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="space-y-2.5 mt-7">
            <button
              data-testid="landing-start-btn"
              onClick={() => navigate("/auth?mode=register")}
              className="w-full rounded-full bg-anima-sagestrong text-anima-ivory py-4 font-medium tracking-wide active:scale-95 transition-transform duration-200"
            >
              {t("landing.start")}
            </button>
            <button
              data-testid="landing-login-btn"
              onClick={() => navigate("/auth?mode=login")}
              className="w-full rounded-full border border-anima-ink/15 text-anima-strong py-3.5 font-medium active:scale-95 transition-transform duration-200"
            >
              {t("landing.haveAccount")}
            </button>
            <InstallPWA variant="link" />
          </div>

          <section data-testid="landing-ianas-section" className="mt-16 md:mt-20">
            <div className="h-px w-10 bg-anima-gold/60 mb-6" />
            <p className="text-[11px] uppercase tracking-[0.25em] text-anima-goldstrong mb-3">
              {t("landing.ianasKicker")}
            </p>
            <h2 className="font-serif text-4xl text-anima-strong leading-none">Ianas</h2>
            <p className="font-serif text-xl text-anima-soft italic mt-1">{t("landing.ianasSubtitle")}</p>

            <figure className="my-8">
              <img
                src="/ianas.jpg"
                alt={t("landing.ianasAlt")}
                className="w-full mx-auto h-auto block rounded-2xl shadow-[0_16px_48px_rgba(74,69,67,0.16)]"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                style={{ userSelect: "none" }}
              />
              <figcaption className="text-center text-[12px] text-anima-soft leading-relaxed mt-4">
                {t("landing.ianasCaption")}
                <br />© Anna Checchi
              </figcaption>
            </figure>

            <p className="text-sm text-anima-strong leading-relaxed mt-4">
              {t("landing.ianasBody")}
            </p>
            <button
              data-testid="landing-ianas-btn"
              onClick={() => navigate("/ianas")}
              className="mt-5 text-[15px] text-anima-sagestrong font-medium"
            >
              {t("landing.ianasBtn")}
            </button>
          </section>
        </motion.div>

        <p className="mt-auto pt-5 flex items-center justify-center gap-1.5 text-[11px] text-anima-soft text-center leading-snug">
          <ShieldCheck size={13} strokeWidth={1.6} className="shrink-0" />
          {t("landing.disclaimer")}
        </p>
      </div>
    </div>
  );
}
